import {fuzzyScore} from '@github-ui/fuzzy-score/fuzzy-score'
import {hasMatch} from 'fzy.js'

import {Strings} from '../constants/strings'
import type {FilterQuery} from '../filter-query'
import {
  type AnyBlock,
  type ARIAFilterSuggestion,
  type FilterBlock,
  type FilterConfig,
  type FilterKey,
  FilterOperator,
  type FilterProvider,
  FilterProviderType,
  type FilterSuggestion,
  type FilterValueData,
  FilterValueType,
  type IndexedBlockValueItem,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  ValidationTypes,
  type ValueRowProps,
} from '../types'
import {
  getExcludeKeySuggestion,
  getFilterValue,
  getHasValueSuggestion,
  getLastFilterBlockValue,
  getNoValueSuggestion,
  getUnescapedFilterValue,
  isEmptyFilterBlockValue,
  isFilterBlock,
} from '../utils'
import {ValueIcon} from '../utils/ValueIcon'
import {RootFilterProvider} from './root'

const operators = ['>=', '<=', '>', '<']

export class StaticFilterProvider extends RootFilterProvider implements FilterProvider {
  filterValues: ARIAFilterSuggestion[]

  constructor(filterKey: FilterKey, filterValues: FilterSuggestion[], options?: SuppliedFilterProviderOptions) {
    super(filterKey, options)
    this.filterValues = filterValues.map(fv => ({...fv, ariaLabel: `${fv.displayName}, ${this.displayName}`}))
  }

  getfilterKeys(): FilterKey[] {
    return [
      {
        key: this.key,
        aliases: this.aliases,
        displayName: this.displayName,
        description: this.description,
        priority: this.priority,
        icon: this.icon,
        options: this.options,
      },
    ]
  }

  getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    if (!this.shouldGetSuggestions(filterBlock)) return null

    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const filterProviderKey = (filterBlock as FilterBlock).provider.key
    const suggestions: ARIAFilterSuggestion[] = []

    if (
      isEmptyFilterBlockValue(filterBlock) &&
      this.options.filterTypes.valueless &&
      !filterBlock.raw.startsWith('-')
    ) {
      suggestions.push(getNoValueSuggestion(this.displayName, this.icon))
    }

    if (isEmptyFilterBlockValue(filterBlock) && this.options.filterTypes.hasValue && !filterBlock.raw.startsWith('-')) {
      suggestions.push(getHasValueSuggestion(this.displayName, this.icon))
    }

    if (
      this.options.filterTypes.exclusive &&
      lastValue === '' &&
      isFilterBlock(filterBlock) &&
      !filterBlock.raw.startsWith('-')
    ) {
      const excludeKeySuggestion = getExcludeKeySuggestion(filterProviderKey)
      suggestions.unshift(excludeKeySuggestion)
    }

    if (!this.allowsFilterValues()) {
      return suggestions
    }

    const inputOperator = operators.find(operator => lastValue === operator || lastValue === `${operator}@`)

    if (!lastValue && !inputOperator) return suggestions.concat(this.filterValues)

    for (const filterValue of this.filterValues) {
      const {value: fValue, displayName} = filterValue
      const value = getFilterValue(fValue)

      const suggestion = {...filterValue, type: FilterValueType.Value}
      const hasOperator = value && !operators.find(operator => value.startsWith(operator))

      // If the filter is a date filter and the input has a operator,
      // we want to include the modifier in the suggestion
      if (this.type === FilterProviderType.Date && hasOperator) {
        suggestion.value = `${inputOperator ?? ''}${value}`
      } else {
        const matchesValue = !!value && hasMatch(lastValue, value)
        const matchesAlias =
          !!config.aliasMatching && filterValue.aliases && filterValue.aliases.some(alias => hasMatch(lastValue, alias))
        const matchesDisplayName = !!displayName && hasMatch(lastValue, displayName)

        if (!value || (!matchesValue && !matchesDisplayName && !matchesAlias)) continue
      }

      filterValue.priority -= fuzzyScore(lastValue, value)

      suggestions.push(suggestion)
    }

    return suggestions
  }

  validateFilterBlockValues(
    filterQuery: FilterQuery,
    block: FilterBlock,
    values: IndexedBlockValueItem[],
    _: FilterConfig,
  ): IndexedBlockValueItem[] {
    return values.map((v, i) => {
      if (!this.allowsFilterValues()) {
        return {
          ...v,
          valid: false,
          validations: [{type: ValidationTypes.InvalidValue, message: Strings.filterValuesUnsupported(this.key)}],
        }
      }

      if (
        !this.options.filterTypes.multiValue &&
        ((block.operator === FilterOperator.Between && i > 1) || (block.operator !== FilterOperator.Between && i > 0))
      ) {
        return getMultiValueValidation(v, this.key)
      }

      const extractedValue = getUnescapedFilterValue(v.value)

      if (!extractedValue) return getEmptyValueValidation(v, this.key)

      if (block.operator === FilterOperator.Between && operators.some(op => extractedValue.startsWith(op))) {
        return getInvalidValueValidation(v, extractedValue, this.key)
      }

      return {...v, valid: true}
    })
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      description: value.description,
      leadingVisual: ValueIcon({value}),
    }
  }
}

export const getMultiValueValidation = (blockValueItem: IndexedBlockValueItem, key: string) => {
  return {
    ...blockValueItem,
    valid: false,
    validations: [{type: ValidationTypes.MultiValueUnsupported, message: Strings.filterMultiValueFalse(key)}],
  }
}

export const getEmptyValueValidation = (blockValueItem: IndexedBlockValueItem, key: string): IndexedBlockValueItem => {
  return {
    ...blockValueItem,
    valid: false,
    validations: [{type: ValidationTypes.EmptyValue, message: Strings.filterValueEmpty(key)}],
  }
}

export const getInvalidValueValidation = (
  blockValueItem: IndexedBlockValueItem,
  extractedValue: string,
  key: string,
): IndexedBlockValueItem => {
  return {
    ...blockValueItem,
    valid: false,
    validations: [
      {
        type: ValidationTypes.InvalidValue,
        message: Strings.filterInvalidValue(key, extractedValue),
      },
    ],
  }
}
